import { useState, useCallback, useMemo, useEffect, useRef } from 'react';

export const REQUIRED_FIELD_ERROR = 'This field is required.';

const withoutValidFields = (errors, form) => {
  const validNames = Object.keys(errors).filter(name => form?.elements.namedItem(name)?.validity?.valid);

  if (validNames.length === 0) return errors;

  const next = { ...errors };

  validNames.forEach(name => delete next[name]);

  return next;
};

/**
 * Replaces the browser bubble of native `required` validation with errors for `FieldError`.
 * Spread `formProps` on the `<form>` and pass `errors[name]` to the field.
 * The browser still blocks submit while required fields are empty.
 * An error disappears once its field becomes valid (typed in or set by state, e.g. A date picker).
 */
const useRequiredFieldErrors = () => {
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);

  const handleInvalid = useCallback(event => {
    const { name, form } = event.target;

    event.preventDefault();
    formRef.current = form;
    if (name) setErrors(prev => ({ ...prev, [name]: REQUIRED_FIELD_ERROR }));
  }, []);

  const handleInput = useCallback(() => {
    setErrors(prev => withoutValidFields(prev, formRef.current));
  }, []);

  // Fields driven by state (date pickers, autocompletes) change without a native input event.
  useEffect(() => {
    handleInput();
  });

  const formProps = useMemo(
    () => ({ onInvalidCapture: handleInvalid, onInputCapture: handleInput, onChangeCapture: handleInput }),
    [handleInvalid, handleInput],
  );

  return { errors, formProps };
};

export default useRequiredFieldErrors;
